/**
 * A linear-syntax parser for the kernel's expression language, for tests and
 * later callers (#361). It keeps every distinction the grader depends on:
 *
 *   - `x3` is ONE variable named `x3`. Identifiers are maximal-munch; `xy` is a
 *     variable named `xy`. Only a NUMBER followed by an identifier or `(` is
 *     implicit multiplication (`2x`, `3(x+1)`), and not after a division
 *     (`1/2x` is ambiguous and is refused, not guessed).
 *   - `x^3`, `x**3`, `x³` are `pow(x, 3)`. Exponents are integer literals only
 *     (`x^y`, `x^(1/2)` are outside the domain -> ParseError).
 *   - `0.5` is a decimal CONSTANT, a different form from `1/2` (a division).
 *   - `(1, 2)` is a tuple, `{1, 2}` is a set; they are different Values.
 *   - `i` is a variable unless `imaginaryUnit: true` is passed.
 *
 * Nothing here evaluates; `parseExpr` returns an AST. Unknown characters,
 * function application and anything else outside the domain throw
 * `ParseError` -- a parse failure is "not an answer in a form we can check",
 * never a silent reinterpretation.
 */

import { CI, Q0, cFromInt, qFromDecimalString, type C } from './numbers.js';
import type { Constraint, Relation } from './certificate.js';
import type { Expr } from './expr.js';

export class ParseError extends Error {
  override readonly name = 'ParseError';
  readonly code: string;
  readonly position: number;
  constructor(code: string, position: number) {
    super(`parse error (${code}) at ${position.toString()}`);
    this.code = code;
    this.position = position;
  }
}

export type Value =
  | { readonly k: 'expr'; readonly e: Expr }
  | { readonly k: 'set'; readonly items: readonly Value[] }
  | { readonly k: 'tuple'; readonly items: readonly Value[] };

export interface Equation {
  readonly lhs: Expr;
  readonly rhs: Expr;
}

export interface ParseOptions {
  /** Treat the identifier `i` as the imaginary unit instead of a variable. */
  readonly imaginaryUnit?: boolean;
}

interface Token {
  readonly t: 'int' | 'dec' | 'id' | 'op' | 'eof';
  readonly text: string;
  readonly pos: number;
}

const SUPERSCRIPTS: Readonly<Record<string, string>> = {
  '⁰': '0',
  '¹': '1',
  '²': '2',
  '³': '3',
  '⁴': '4',
  '⁵': '5',
  '⁶': '6',
  '⁷': '7',
  '⁸': '8',
  '⁹': '9',
};

const OP_ALIASES: Readonly<Record<string, string>> = {
  '−': '-',
  '–': '-',
  '×': '*',
  '·': '*',
  '⋅': '*',
  '÷': '/',
};

const ID_START = /[\p{L}_]/u;
const ID_PART = /[\p{L}0-9_]/u;

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  const chars = Array.from(src);
  let i = 0;
  while (i < chars.length) {
    const ch = chars[i] as string;
    if (/\s/u.test(ch)) {
      i += 1;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      let j = i;
      while (j < chars.length && (chars[j] as string) >= '0' && (chars[j] as string) <= '9') j += 1;
      if (chars[j] === '.' && j + 1 < chars.length && (chars[j + 1] as string) >= '0' && (chars[j + 1] as string) <= '9') {
        j += 1;
        while (j < chars.length && (chars[j] as string) >= '0' && (chars[j] as string) <= '9') j += 1;
        out.push({ t: 'dec', text: chars.slice(i, j).join(''), pos: i });
      } else {
        out.push({ t: 'int', text: chars.slice(i, j).join(''), pos: i });
      }
      i = j;
      continue;
    }
    if (ID_START.test(ch)) {
      let j = i + 1;
      while (j < chars.length && ID_PART.test(chars[j] as string)) j += 1;
      out.push({ t: 'id', text: chars.slice(i, j).join(''), pos: i });
      i = j;
      continue;
    }
    if (SUPERSCRIPTS[ch] !== undefined) {
      let j = i;
      let digits = '';
      while (j < chars.length && SUPERSCRIPTS[chars[j] as string] !== undefined) {
        digits += SUPERSCRIPTS[chars[j] as string] as string;
        j += 1;
      }
      out.push({ t: 'op', text: '^', pos: i });
      out.push({ t: 'int', text: digits, pos: i });
      i = j;
      continue;
    }
    if (ch === '*' && chars[i + 1] === '*') {
      out.push({ t: 'op', text: '^', pos: i });
      i += 2;
      continue;
    }
    const pair = ch + (chars[i + 1] ?? '');
    if (pair === '<=' || pair === '>=' || pair === '!=') {
      out.push({ t: 'op', text: pair === '<=' ? '≤' : pair === '>=' ? '≥' : '≠', pos: i });
      i += 2;
      continue;
    }
    const aliased = OP_ALIASES[ch] ?? ch;
    if ('+-*/^(){},=<>≤≥≠'.includes(aliased)) {
      out.push({ t: 'op', text: aliased, pos: i });
      i += 1;
      continue;
    }
    throw new ParseError('unexpected_character', i);
  }
  out.push({ t: 'eof', text: '', pos: chars.length });
  return out;
}

class Parser {
  private readonly tokens: Token[];
  private index = 0;
  private readonly imaginaryUnit: boolean;

  constructor(src: string, options: ParseOptions) {
    this.tokens = tokenize(src);
    this.imaginaryUnit = options.imaginaryUnit === true;
  }

  private peek(offset = 0): Token {
    const at = this.index + offset;
    return (at < this.tokens.length ? this.tokens[at] : this.tokens[this.tokens.length - 1]) as Token;
  }

  private next(): Token {
    const token = this.peek();
    if (token.t !== 'eof') this.index += 1;
    return token;
  }

  private isOp(text: string, offset = 0): boolean {
    const token = this.peek(offset);
    return token.t === 'op' && token.text === text;
  }

  private expectOp(text: string): void {
    if (!this.isOp(text)) throw new ParseError(`expected_${text}`, this.peek().pos);
    this.next();
  }

  expectEof(): void {
    if (this.peek().t !== 'eof') throw new ParseError('unexpected_trailing_input', this.peek().pos);
  }

  atEof(): boolean {
    return this.peek().t === 'eof';
  }

  // ---- expressions ----

  parseExprTop(): Expr {
    return this.expr();
  }

  private expr(): Expr {
    let left = this.term();
    while (this.isOp('+') || this.isOp('-')) {
      const op = this.next().text;
      const right = this.term();
      left = op === '+' ? { k: 'add', a: left, b: right } : { k: 'sub', a: left, b: right };
    }
    return left;
  }

  private startsFactor(): boolean {
    const token = this.peek();
    return token.t === 'id' || token.t === 'int' || token.t === 'dec' || this.isOp('(');
  }

  private term(): Expr {
    let left = this.unary();
    let sawDivision = false;
    // Whether the last factor was a bare number or a parenthesised group:
    // the only things implicit multiplication may follow.
    let lastCanJuxtapose = this.lastWasNumberOrGroup;
    for (;;) {
      if (this.isOp('*') || this.isOp('/')) {
        const op = this.next().text;
        const right = this.unary();
        if (op === '/') sawDivision = true;
        left = op === '*' ? { k: 'mul', a: left, b: right } : { k: 'div', a: left, b: right };
        lastCanJuxtapose = this.lastWasNumberOrGroup;
        continue;
      }
      if (this.startsFactor()) {
        if (!lastCanJuxtapose) {
          // `x y`, `x(y)`: a variable followed by a factor. `x(` would be a function call.
          throw new ParseError(this.isOp('(') ? 'function_application' : 'implicit_multiplication_not_allowed', this.peek().pos);
        }
        const next = this.peek();
        if (next.t === 'int' || next.t === 'dec') throw new ParseError('adjacent_numbers', next.pos);
        if (sawDivision) throw new ParseError('ambiguous_implicit_multiplication', next.pos);
        const right = this.powerOperand();
        left = { k: 'mul', a: left, b: right };
        lastCanJuxtapose = this.lastWasNumberOrGroup;
        continue;
      }
      return left;
    }
  }

  private lastWasNumberOrGroup = false;

  private unary(): Expr {
    if (this.isOp('-')) {
      this.next();
      const operand = this.unary();
      return { k: 'neg', a: operand };
    }
    if (this.isOp('+')) {
      this.next();
      return this.unary();
    }
    return this.powerOperand();
  }

  private powerOperand(): Expr {
    const base = this.atom();
    if (this.isOp('^')) {
      this.next();
      const exp = this.exponent();
      this.lastWasNumberOrGroup = false;
      const result: Expr = { k: 'pow', base, exp };
      if (this.isOp('^')) throw new ParseError('nested_exponent', this.peek().pos);
      return result;
    }
    return base;
  }

  private exponent(): bigint {
    let parenthesised = false;
    if (this.isOp('(')) {
      this.next();
      parenthesised = true;
    }
    let negative = false;
    if (this.isOp('-')) {
      this.next();
      negative = true;
    } else if (this.isOp('+')) {
      this.next();
    }
    const token = this.peek();
    if (token.t !== 'int') throw new ParseError('exponent_not_integer_literal', token.pos);
    this.next();
    if (parenthesised) this.expectOp(')');
    const magnitude = BigInt(token.text);
    return negative ? -magnitude : magnitude;
  }

  private atom(): Expr {
    const token = this.peek();
    if (token.t === 'int') {
      this.next();
      this.lastWasNumberOrGroup = true;
      return { k: 'const', v: cFromInt(BigInt(token.text)), form: 'integer' };
    }
    if (token.t === 'dec') {
      this.next();
      this.lastWasNumberOrGroup = true;
      const value: C = { re: qFromDecimalString(token.text), im: Q0 };
      return { k: 'const', v: value, form: 'decimal' };
    }
    if (token.t === 'id') {
      this.next();
      this.lastWasNumberOrGroup = false;
      if (this.imaginaryUnit && token.text === 'i') return { k: 'const', v: CI, form: 'imaginary_unit' };
      return { k: 'var', name: token.text };
    }
    if (this.isOp('(')) {
      this.next();
      const inner = this.expr();
      this.expectOp(')');
      this.lastWasNumberOrGroup = true;
      return inner;
    }
    throw new ParseError('expected_operand', token.pos);
  }

  // ---- values ----

  /** Does the `(` at the current position open a tuple (a comma at depth 1)? */
  private parenIsTuple(): boolean {
    let depth = 0;
    for (let k = this.index; k < this.tokens.length; k += 1) {
      const token = this.tokens[k] as Token;
      if (token.t !== 'op') continue;
      if (token.text === '(' || token.text === '{') depth += 1;
      else if (token.text === ')' || token.text === '}') {
        depth -= 1;
        if (depth === 0) return false;
      } else if (token.text === ',' && depth === 1) return true;
    }
    return false;
  }

  value(): Value {
    if (this.isOp('{')) {
      this.next();
      const items: Value[] = [];
      if (!this.isOp('}')) {
        items.push(this.value());
        while (this.isOp(',')) {
          this.next();
          items.push(this.value());
        }
      }
      this.expectOp('}');
      return { k: 'set', items };
    }
    if (this.isOp('(') && this.parenIsTuple()) {
      this.next();
      const items: Value[] = [this.value()];
      while (this.isOp(',')) {
        this.next();
        items.push(this.value());
      }
      this.expectOp(')');
      return { k: 'tuple', items };
    }
    return { k: 'expr', e: this.expr() };
  }

  constraint(): Constraint {
    const lhs = this.expr();
    const token = this.peek();
    if (token.t !== 'op' || !RELATIONS.has(token.text)) throw new ParseError('expected_relation', token.pos);
    this.next();
    const rhs = this.expr();
    return { lhs, rel: token.text as Relation, rhs };
  }

  equation(): Equation {
    const lhs = this.expr();
    this.expectOp('=');
    const rhs = this.expr();
    if (this.isOp('=')) throw new ParseError('chained_equality', this.peek().pos);
    return { lhs, rhs };
  }
}

const RELATIONS: ReadonlySet<string> = new Set(['=', '≠', '<', '≤', '>', '≥']);

/** `lhs REL rhs` with REL one of `= != < <= > >=` (or ≠ ≤ ≥). */
export function parseConstraint(src: string, options: ParseOptions = {}): Constraint {
  const parser = new Parser(src, options);
  const c = parser.constraint();
  parser.expectEof();
  return c;
}

export function parseExpr(src: string, options: ParseOptions = {}): Expr {
  const parser = new Parser(src, options);
  const e = parser.parseExprTop();
  parser.expectEof();
  return e;
}

export function parseValue(src: string, options: ParseOptions = {}): Value {
  const parser = new Parser(src, options);
  const v = parser.value();
  parser.expectEof();
  return v;
}

export function parseEquation(src: string, options: ParseOptions = {}): Equation {
  const parser = new Parser(src, options);
  const eq = parser.equation();
  parser.expectEof();
  return eq;
}

export function valueEqualStructurally(a: Value, b: Value, exprEq: (x: Expr, y: Expr) => boolean): boolean {
  if (a.k !== b.k) return false;
  if (a.k === 'expr' && b.k === 'expr') return exprEq(a.e, b.e);
  if (a.k !== 'expr' && b.k !== 'expr') {
    return (
      a.items.length === b.items.length &&
      a.items.every((item, index) => valueEqualStructurally(item, b.items[index] as Value, exprEq))
    );
  }
  return false;
}
